'use client'

import dynamic from 'next/dynamic'
import { BrandContextProvider } from '@/features/brands/brand-context'
import { LP_V3_NAV_HEIGHT } from '@/features/landing-page/v3/landing-v3-nav-constants'
import { LoadingScreen } from '@/features/loading/loading-screen'
import { usePathname } from 'next/navigation'
import { PUBLIC_LANDING_ROUTES, UrlConfig } from '@/setup/routing/routing'
import { LandingPageProvider } from '@/features/landing-page/landing-page-context'
import {
    LandingMediaOverlayProvider,
    useLandingMediaOverlay,
} from '@/features/landing-page/landing-media-overlay-context'
import { LandingAuthProvider } from '@/features/landing-page/landing-auth-context'
import { LandingAuthContent } from '@/features/landing-page/landing-auth-content'
import { LandingScrollReset } from '@/features/landing-page/landing-scroll-reset'
import {
    LandingCarouselAssetsProvider,
    type ILandingCarouselAssetsContextValue,
} from '@/features/landing-page/landing-carousel-assets-context'
import type { ILandingCarouselSlugAssets } from '@/features/landing-page/helpers/get-carousel-assets-server'
import { cn } from '@/utils/styles/cn'
import { LpAboveFooterSection } from '@/features/landing-page/v3/sections/lp-above-footer-section'
import { LpHomeSections } from '@/features/landing-page/v3/sections/lp-home-sections'
import { LpPricingSections } from '@/features/landing-page/v3/sections/lp-pricing-sections'
import { LpFooter } from '@/features/landing-page/v3/sections/lp-footer'
import { LandingV3NavBar } from '@/features/landing-page/v3/landing-v3-nav-bar'
import { LandingDefaultHeroSection } from '@/features/landing-page/landing-default-hero-section'
import { LandingPageNavBar, NAV_BAR_HEIGHT } from '@/features/landing-page/landing-page-nav-bar'
import { LandingPageFooter } from '@/features/landing-page/landing-page-footer'
import { LandingSuperUserMenu } from '@/features/landing-page/landing-superuser-menu'
import { isAuthBackdropRoute, isV3EligibleRoute, LANDING_DESIGN } from '@/features/landing-page/landing-design'
import { useLandingDesign } from '@/features/landing-page/use-landing-design'
import { Fragment, useEffect } from 'react'
import { useAuth } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import { POST_AUTH_ROUTING_STORAGE_KEY } from '@/features/auth/helpers/auth-constants'
import { completePendingPostAuthNavigation } from '@/features/auth/helpers/complete-post-auth-navigation'

// Meta LP sections: lazy so default landing doesn't pay for Lp* bundle (framer-motion, etc.)
function LazyOverlayViewGate() {
    const { isOpen } = useLandingMediaOverlay()
    return isOpen ? <LandingMediaOverlayViewLazy /> : null
}

// Overlay view: lazy so JS (framer-motion, Video, etc.) loads only when user opens overlay
const LandingMediaOverlayViewLazy = dynamic(
    () => import('@/features/landing-page/landing-media-overlay-view').then((m) => m.LandingMediaOverlayView),
    { ssr: false }
)

// Old-design fallback for the SUPERUSER PREVIEW when LANDING_DESIGN === 'new' but the previewer
// toggles to 'old'. The gated `LandingDefaultSections` short-circuits to null server-side once the
// flag is 'new' — we import the unguarded body here so the preview still renders client-side.
// Mirrors how <LpHomeSections /> was client-mounted for the new-design preview when flag was 'old'.
const LandingOldSectionsPreview = dynamic(
    () =>
        import('@/features/landing-page/landing-default-sections').then((m) => m.LandingDefaultSectionsBody),
    { ssr: false }
)

function CarouselAssetsWrapper({
    initialCarouselAssets,
    children,
}: {
    initialCarouselAssets?: Record<string, ILandingCarouselSlugAssets> | null
    children: React.ReactNode
}) {
    if (!initialCarouselAssets || Object.keys(initialCarouselAssets).length === 0) return <>{children}</>
    const value: ILandingCarouselAssetsContextValue = { bySlug: initialCarouselAssets }
    return <LandingCarouselAssetsProvider value={value}>{children}</LandingCarouselAssetsProvider>
}

/**
 * Routes that render in the full lp-v3 chrome (v3 nav + footer + design tokens) even while the
 * global LANDING_DESIGN switch is still 'old'. These pages are built in the v3 design language,
 * so the old chrome would clash. Redundant (and droppable) once the switch flips to 'new'.
 */
const isForceV3ChromeRoute = (pathname: string) =>
    pathname === PUBLIC_LANDING_ROUTES.PARTNER_PROGRAM ||
    pathname === PUBLIC_LANDING_ROUTES.D2C ||
    pathname === PUBLIC_LANDING_ROUTES.AGENCY ||
    pathname === PUBLIC_LANDING_ROUTES.AI_ADS

const PASSTHROUGH_ROUTES = [
    UrlConfig['/auth/sso-callback'].getUrl(),
    UrlConfig['/auth/oauth2/callback'].getUrl(),
    UrlConfig['/auth/logout'].getUrl(),
    UrlConfig['/auth/verify-invite'].getUrl(),
    // The Klaviyo marketplace install trampoline renders only a loading screen while it resolves
    // auth/org/brand and redirects into the OAuth flow — no landing chrome.
    PUBLIC_LANDING_ROUTES.INTEGRATIONS_KLAVIYO_INSTALL,
] as const

interface ILandingPageClientShellProps {
    children: React.ReactNode
    /** Route-specific sections from @sections parallel route (null for custom-only pages) */
    sections: React.ReactNode
    /** Server-rendered v3 sections (present only when LANDING_DESIGN === 'new'); else the preview client-renders them. */
    v3Sections: React.ReactNode
    /** Server-rendered footer */
    footer: React.ReactNode
    /** Server-resolved slug-keyed carousel assets (seeded from the static manifest) */
    initialCarouselAssets?: Record<string, ILandingCarouselSlugAssets> | null
    /** Optional className for top-level wrapper (used for design system scoping) */
    className?: string
    /** Server-rendered nav bar (lp-v3), passed in as a prop so it stays a Server Component. */
    nav: React.ReactNode
}

export function LandingPageClientShell({
    children,
    sections,
    v3Sections,
    footer,
    initialCarouselAssets,
    className,
    nav,
}: ILandingPageClientShellProps) {
    const pathname = usePathname()
    const { effective } = useLandingDesign()

    const isPassthroughRoute = PASSTHROUGH_ROUTES.some((route) => pathname.startsWith(route))

    if (isPassthroughRoute) {
        return (
            <Fragment>
                <LoadingScreen />
                {/* Mount passthrough route so its logic runs (e.g. redirect, logout) */}
                {children}
            </Fragment>
        )
    }

    return (
        <BrandContextProvider>
            <style>{`body::before { display: none !important; }`}</style>
            {/* Paint the page BEHIND the shell cream too (overscroll bands, portal backdrops) — new
                design + logged-out shell only; the logged-in app never mounts this component. */}
            {effective === 'new' && <style>{`body { background-color: #fdfbf8; }`}</style>}
            <LandingPageProvider>
                <LandingMediaOverlayProvider>
                    <CarouselAssetsWrapper initialCarouselAssets={initialCarouselAssets}>
                        {/* `lp-v3` (redesign tokens + cream background) is on for the new design,
                            plus the routes that render in v3 chrome ahead of the flip. */}
                        <div
                            className={cn(
                                'min-h-screen flex flex-col bg-background',
                                className,
                                (effective === 'new' || isForceV3ChromeRoute(pathname)) && 'lp-v3'
                            )}
                        >
                            <InnerLayout nav={nav} sections={sections} v3Sections={v3Sections} footer={footer}>
                                {children}
                            </InnerLayout>
                        </div>

                        <LazyOverlayViewGate />
                        {/* Shift+Cmd+D superuser menu to preview the old/new landing design (superusers only) */}
                        <LandingSuperUserMenu />
                    </CarouselAssetsWrapper>
                </LandingMediaOverlayProvider>
            </LandingPageProvider>
        </BrandContextProvider>
    )
}

interface IInnerLayoutProps {
    children: React.ReactNode
    nav: React.ReactNode
    sections: React.ReactNode
    v3Sections: React.ReactNode
    footer: React.ReactNode
}

const InnerLayout = ({ children, nav, sections, v3Sections, footer }: IInnerLayoutProps) => {
    const pathname = usePathname()
    const router = useRouter()
    const { isSignedIn, isLoaded } = useAuth()
    const { effective } = useLandingDesign()

    // Eligible pages render the new design (v3 hero handled in LandingDefaultHeroSection + v3 sections
    // here). `v3Sections` is the SSR'd tree when LANDING_DESIGN === 'new'; for a superuser preview
    // (constant still 'old') it's null, so we client-render LpHomeSections.
    // Sign-in / sign-up render the full home body underneath the auth dialog — hero + v3 sections.
    const showNewDesign = effective === 'new' && (isV3EligibleRoute(pathname) || isAuthBackdropRoute(pathname))
    // /pricing is a custom-children route (own body), so it is NOT v3-"eligible" — but under the new
    // design it swaps to its OWN v3 body (real billing plans + FAQ) instead of the old pricing page.
    // Plans are client-fetched with a skeleton; TODO(landing-launch): seed them server-side via a
    // per-route v3 slot so they ship in the SSR HTML (see LpBillingPlans).
    const showNewPricing = effective === 'new' && pathname === PUBLIC_LANDING_ROUTES.PRICING

    useEffect(() => {
        if (pathname !== '/' || !isLoaded || !isSignedIn) return

        const pending = sessionStorage.getItem(POST_AUTH_ROUTING_STORAGE_KEY)
        if (!pending) return

        sessionStorage.removeItem(POST_AUTH_ROUTING_STORAGE_KEY)

        if (pending.startsWith('/') && !pending.startsWith('//')) {
            router.replace(pending)
            return
        }

        completePendingPostAuthNavigation(pending)
    }, [pathname, isSignedIn, isLoaded, router])

    // The nav / footer / background switch globally with the design (v3 chrome for new, the old
    // landing nav+footer+app-background for old). `nav`/`footer` are the SSR'd v3 chrome when
    // LANDING_DESIGN==='new'; for a superuser preview they're null so we client-render the v3 chrome.
    const isNew = effective === 'new'

    // Force-v3 routes (see isForceV3ChromeRoute) carry the full v3 chrome even when the effective
    // design is 'old' (e.g. the superuser preview toggle) — their bodies are built in the v3
    // language and the old chrome clashes. `nav`/`footer` are null in that case, so the v3 chrome
    // client-renders here, exactly like the superuser preview path.
    const v3Chrome = isNew || isForceV3ChromeRoute(pathname)

    return (
        <LandingAuthProvider>
            <LandingScrollReset />
            {v3Chrome ? (nav ?? <LandingV3NavBar />) : <LandingPageNavBar />}

            <main
                // `overflow-x: clip` clips any horizontal overflow (full-bleed 100vw sections, the wide
                // logo marquee) so no horizontal scrollbar / white gutter appears — without creating a
                // scroll container, so the statement section's `sticky` pin keeps working.
                className="flex-1 flex flex-col overflow-x-clip"
                style={{ marginTop: v3Chrome ? LP_V3_NAV_HEIGHT : NAV_BAR_HEIGHT }}
            >
                <LandingDefaultHeroSection />
                {showNewPricing ? (
                    <LpPricingSections />
                ) : showNewDesign ? (
                    (v3Sections ?? <LpHomeSections />)
                ) : (
                    <>
                        {children}
                        {/* Under LANDING_DESIGN === 'new' the old @sections tree is gated to null
                            server-side, so `sections` renders nothing. Client-mount the old body
                            only for the superuser "preview old" toggle (effective === 'old');
                            public 'new' visitors on non-eligible routes get nothing extra. Under
                            'old' the SSR'd `{sections}` tree is used as before.
                            (The `?? null` fallback in the older code was dead — parallel-route
                            slot props are ALWAYS truthy React elements, so the null-coalesce
                            never fires; gate on LANDING_DESIGN/effective instead.) */}
                        {LANDING_DESIGN === 'new'
                            ? effective === 'old' &&
                              !pathname.startsWith('/auth') && <LandingOldSectionsPreview />
                            : sections}
                    </>
                )}
            </main>

            {/* Auth modal — always mounted for all landing routes (home, pricing, agency, etc.) */}
            <LandingAuthContent />

            {/* lp-v3 fanned-cards showcase that tucks behind the footer — like the footer, it closes
                EVERY logged-out page under the new design (home, products, pricing, playbooks, …);
                only the auth pages skip it. */}
            {isNew && (!pathname.startsWith('/auth') || isAuthBackdropRoute(pathname)) && <LpAboveFooterSection />}

            {v3Chrome ? (footer ?? <LpFooter />) : <LandingPageFooter />}
        </LandingAuthProvider>
    )
}
